import { NextPage } from "next";
import DetailPage from "components/pages/detail/Index";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { GetServerSidePropsContext } from "next";
import { DetailInfo } from "interfaces/detail";
import { authOptions } from "pages/api/auth/[...nextauth]";
import { getServerSession } from "next-auth";

import { useEffect } from "react";
import { useDetailData } from "utils/clientAPIs/detailDiagnosis";
import { useRecoilState } from "recoil";
import { detailLoadingState } from "states/detailLoading";
import { KOREA } from "utils/constant/ITME_INFO";

interface Props {
  isMobile: boolean;
  detailInfo?: DetailInfo;
  IS_5MIN?: boolean;
  detailRiskChartData?: any;
  threeDaysAgoData?: any;
  realTimeHistoryData?: any;
  realTimeTwodaysHistoryData?: any;
  isHoliday?: boolean;
  riskTargetDate?: any;
  isNoPortUser?: boolean;
  sCode?: any;
  name?: string;
  isLoginUser?: boolean;
  orderCategory?: any;
  aiData?: any;
  ticker?: string;
  /** GSSP 초기 detail 배열 — SWR fallback / SSR 본문용 */
  initialDetailData?: any[] | null;
  initialScoreData?: any | null;
  initialHalt?: { isHalt?: boolean } | null;
}

const Detail: NextPage<Props> = ({
  isMobile,
  detailInfo,
  sCode,
  realTimeTwodaysHistoryData,
  isNoPortUser,
  name,
  isHoliday,
  riskTargetDate,
  isLoginUser,
  orderCategory,
  aiData,
  ticker,
  initialDetailData,
  initialScoreData,
  initialHalt,
}) => {
  const [loading, setLoading] = useRecoilState(
    detailLoadingState(ticker as string)
  );

  const {
    detailData,
    prevDayData,
    isLoading,
    isHalt,
    isValidScoreStock,
    scoreData,
  } = useDetailData(ticker as string, {
    fallbackDetail: initialDetailData ?? undefined,
    fallbackScore: initialScoreData ?? undefined,
    fallbackHalt: initialHalt ?? undefined,
  });

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  return (
    <DetailPage
      scoreData={scoreData}
      isLoading={isLoading}
      detailInfo={detailInfo}
      detailData={detailData}
      prevDayData={prevDayData}
      isHoliday={isHoliday}
      riskTargetDate={riskTargetDate}
      isMobile={isMobile}
      isNoPortUser={isNoPortUser}
      isValidScoreStock={isValidScoreStock}
      isHalt={isHalt}
      sCode={sCode}
      isLoginUser={isLoginUser}
      name={name}
      orderCategory={orderCategory}
      aiData={aiData}
      ticker={ticker}
    />
  );
};

export default Detail;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  let locale = context.locale || context.defaultLocale || "ko";
  const session: any = await getServerSession(
    context.req,
    context.res,
    authOptions
  );
  const ticker = context.query.ticker;
  const base = process.env.NEXTAUTH_URL;

  const res3 = await fetch(`${base}/api/detailInfo?ticker=${ticker}`);
  let detailInf;
  if (res3.status !== 401) {
    detailInf = await res3?.json();
  }
  const detailInfo = detailInf?.length > 0 ? detailInf?.[0] : null;

  const sCode = null;
  const isNoPortUser =
    !session ||
    (session &&
      (!session.user.portfolios || session.user.portfolios.length === 0));

  // meta / order / ai / detail 을 병렬로 — GEO SSR용 detail·score 포함
  const [metaJson, detailList, aiData] = await Promise.all([
    fetch(`${base}/api/detail/meta?ticker=${ticker}`).then((r) => r.json()),
    fetch(`${base}/api/detail/${ticker}`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null),
    fetch(`${base}/api/detail/ai?ticker=${ticker}`).then((r) => r.json()),
  ]);

  const { name, isHoliday, riskTargetDate, loc } = metaJson;
  const LOC = loc === KOREA ? "korea" : "us";
  const itemCode = detailList?.[0]?.ITEM_CD;

  const [orderJson, scoreData, haltData] = await Promise.all([
    fetch(`${base}/api/kb/ordertime?loc=${LOC}`).then((r) => r.json()),
    itemCode
      ? fetch(
          `${base}/api/detail/score?ticker=${ticker}&itemCode=${itemCode}`
        )
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      : Promise.resolve(null),
    itemCode
      ? fetch(`${base}/api/haltyn?itemCode=${itemCode}`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      : Promise.resolve(null),
  ]);

  return {
    props: {
      hasMetaData: true,
      isNoPortUser,
      sCode,
      detailInfo,
      name,
      isHoliday,
      riskTargetDate,
      isLoginUser: session,
      orderCategory: orderJson?.orderCategory ?? null,
      aiData,
      ticker,
      initialDetailData: detailList ?? null,
      initialScoreData: scoreData ?? null,
      initialHalt: haltData ?? null,
      ...(await serverSideTranslations(locale, ["detail", "search"])),
    },
  };
}
